import {
  DefaultTransformer,
  deserialize,
  DocumentObject,
  JsonObject,
  serialize,
  transform,
  Transformer,
} from '@jsonapiworld/jsonapi-serializer';
import { parseISO } from 'date-fns';
import { normalizeF24Details } from '~/api/bookkeeping/f24-details';
import { backendApiGateway } from '~/client/backend';
import { CompanyResourceType } from '~/types/company';
import { MetaDataCursorPage } from '~/types/jsonapi';
import {
  AltriEntiDebitCreditFormPayload,
  AltriEntiDetails,
  CausaleContributoINPS,
  CodiceRegione,
  CodiceSedeINPS,
  CodiceTributo,
  CodiceTributoModality,
  CodiceTributoSection,
  CreateTaxDebitCreditFormInput,
  CreateTaxDebitCreditFormPayload,
  ErarioDebitCreditFormPayload,
  ErarioDetails,
  F24,
  F24CreatePayload,
  F24Details,
  F24QueryFilters,
  F24ResourceType,
  F24Source,
  F24UploadCreatePayload,
  IMUAltriEntiDebitCreditFormPayload,
  IMUAltriEntiDetails,
  INAILAltriEntiDebitCreditFormPayload,
  INAILAltriEntiDetails,
  INPSDebitCreditFormPayload,
  INPSDetails,
  RegioniDetails,
  RegionsDebitCreditFormPayload,
  TaxDebitCredit,
  TaxDebitCreditResourceType,
  TaxDebitCreditsQueryFilters,
} from '~/types/taxes';
import { formatDateForAPI, formatISOForAPI } from '~/utils/formatDate';
import { isNotEmpty } from '~/utils/helpers';
import {
  buildJsonAPIQueryParams,
  IJsonAPIFilterBuilder,
  jsonAPICursorPaginatorBuilder,
  jsonAPIExtraQueryBuilder,
  jsonAPIFilterBuilder,
  jsonAPIIncludeBuilder,
  jsonAPISortBuilder,
} from '~/utils/jsonapi-query';

const TaxDebitCreditsPageSize = 50;

const taxDebitCreditsFiltersToJsonApiFilters = (filters: TaxDebitCreditsQueryFilters): IJsonAPIFilterBuilder => {
  const builder = jsonAPIFilterBuilder();

  if (filters.type === 'debit') {
    builder.gt('debitAmount', 0);
  } else if (filters.type === 'credit') {
    builder.gt('creditAmount', 0);
  }

  if (typeof filters.fullyAllocated !== 'undefined') {
    builder.eq('fullyAllocated', filters.fullyAllocated);
  }

  if (filters.dueDateFrom) builder.gte('dueDate', formatISOForAPI(filters.dueDateFrom));
  if (filters.dueDateTo) builder.lte('dueDate', formatISOForAPI(filters.dueDateTo));

  if (filters.tribute) {
    builder.eq('tribute', filters.tribute);
  }

  return builder;
};

export const fetchTaxDebitCredits = async (
  companyId: string,
  filters: TaxDebitCreditsQueryFilters,
  pageParam: string | null,
): Promise<{ data: TaxDebitCredit[]; meta: MetaDataCursorPage }> => {
  const filterBuilder = taxDebitCreditsFiltersToJsonApiFilters(filters);
  filterBuilder.eq('company.id', companyId);
  const paginatorBuilder = jsonAPICursorPaginatorBuilder({
    size: TaxDebitCreditsPageSize,
    cursor: pageParam || undefined,
  });

  const includeBuilder = jsonAPIIncludeBuilder(['sources']);
  const params = buildJsonAPIQueryParams(filterBuilder, paginatorBuilder, includeBuilder);

  const { data } = await backendApiGateway.get<DocumentObject>('/api/v1/tax-debit-credits', { params });

  const deserialized = deserialize<TaxDebitCredit>(data) as TaxDebitCredit[];

  return {
    data: deserialized,
    meta: data.meta as MetaDataCursorPage,
  };
};

interface BaseCreateTaxDebitCreditPayload {
  companyId: string;
  tribute: string;
  bookkeepingAccountId: string;
  debitAmount: string | null;
  creditAmount: string | null;
  validFrom: string | null;
  dueDate: string | null;
  description: string;
}

interface ErarioCreatePayload extends BaseCreateTaxDebitCreditPayload {
  sectionType: 'erario';
  codiceUfficio: string | null;
  codiceAtto: string | null;
  details: ErarioDetails;
}

interface INPSCreatePayload extends BaseCreateTaxDebitCreditPayload {
  sectionType: 'inps';
  details: INPSDetails;
}

interface RegioniCreatePayload extends BaseCreateTaxDebitCreditPayload {
  sectionType: 'regioni';
  details: RegioniDetails;
}

interface IMUAltriEntiCreatePayload extends BaseCreateTaxDebitCreditPayload {
  sectionType: 'imu_altri_enti';
  details: IMUAltriEntiDetails;
}

interface INAILAltriEntiCreatePayload extends BaseCreateTaxDebitCreditPayload {
  sectionType: 'inail_altri_enti';
  details: INAILAltriEntiDetails;
}

interface AltriEntiCreatePayload extends BaseCreateTaxDebitCreditPayload {
  sectionType: 'altri_enti';
  details: AltriEntiDetails;
}

type CreateTaxDebitCreditPayload =
  | ErarioCreatePayload
  | INPSCreatePayload
  | RegioniCreatePayload
  | IMUAltriEntiCreatePayload
  | INAILAltriEntiCreatePayload
  | AltriEntiCreatePayload;

class CreateTaxDebitCreditTransformer extends Transformer<CreateTaxDebitCreditPayload, {}> {
  constructor() {
    super();
    this.type = TaxDebitCreditResourceType;
    this.relationships = {
      company: ({ companyId }) =>
        transform()
          .withInput({ id: companyId })
          .withTransformer(new DefaultTransformer(CompanyResourceType))
          .toContext(),
      bookkeepingAccount: ({ bookkeepingAccountId }) =>
        transform()
          .withInput({ id: bookkeepingAccountId })
          .withTransformer(new DefaultTransformer('bookkeeping-account'))
          .toContext(),
    };
  }

  transform(payload: CreateTaxDebitCreditPayload): JsonObject {
    const base = {
      sectionType: payload.sectionType,
      tribute: payload.tribute,
      debitAmount: payload.debitAmount,
      creditAmount: payload.creditAmount,
      validFrom: payload.validFrom,
      dueDate: payload.dueDate,
      description: payload.description,
      details: payload.details,
    };

    if (payload.sectionType === 'erario') {
      return {
        ...base,
        codiceUfficio: payload.codiceUfficio,
        codiceAtto: payload.codiceAtto,
      };
    }

    return base;
  }
}

const buildBaseF24PayloadAttrs = (companyId: string, form: CreateTaxDebitCreditFormPayload) => ({
  companyId,
  bookkeepingAccountId: form.bookkeepingAccountId,
  debitAmount: form.type === 'debit' ? form.amount.toFixed(2) : null,
  creditAmount: form.type === 'credit' ? form.amount.toFixed(2) : null,
  validFrom: form.validFrom || null,
  dueDate: form.scadenza || form.validUntil || null,
});

const buildErarioPayload = (companyId: string, form: ErarioDebitCreditFormPayload): ErarioCreatePayload => ({
  ...buildBaseF24PayloadAttrs(companyId, form),
  sectionType: 'erario',
  tribute: form.codiceTributo,
  description: form.description || form.codiceTributo,
  codiceUfficio: form.codiceUfficio || null,
  codiceAtto: form.codiceAtto || null,
  details: {
    codice_tributo: form.codiceTributo,
    codice_ufficio: form.codiceUfficio || null,
    codice_atto: form.codiceAtto || null,
    anno_periodo_riferimento: form.annoPeriodoRiferimento ?? null,
    rateazione_regione_prov_mese_rif: form.rateazioneRegioneProvMeseRif || null,
    importo_a_debito_versato: form.type === 'debit' ? form.amount.toFixed(2) : null,
    importo_a_credito_compensato: form.type === 'credit' ? form.amount.toFixed(2) : null,
  },
});

const buildInpsPayload = (companyId: string, form: INPSDebitCreditFormPayload): INPSCreatePayload => ({
  ...buildBaseF24PayloadAttrs(companyId, form),
  sectionType: 'inps',
  tribute: form.causaleContributo,
  description: form.description || form.causaleContributo,
  details: {
    codice_sede: form.codiceSede || null,
    causale_contributo: form.causaleContributo,
    matricola_inps_filiale_azienda: form.matricolaCodiceFiliale || null,
    periodo_di_riferimento_da_mm_aaaa: form.periodoRiferimentoInizio || null,
    periodo_di_riferimento_a_mm_aaaa: form.periodoRiferimentoFine || null,
    importo_a_debito_versato: form.type === 'debit' ? form.amount.toFixed(2) : null,
    importo_a_credito_compensato: form.type === 'credit' ? form.amount.toFixed(2) : null,
  },
});

const buildRegioniPayload = (companyId: string, form: RegionsDebitCreditFormPayload): RegioniCreatePayload => ({
  ...buildBaseF24PayloadAttrs(companyId, form),
  sectionType: 'regioni',
  tribute: form.codiceTributo,
  description: form.description || form.codiceTributo,
  details: {
    codice_regione: form.codiceRegione || null,
    codice_tributo: form.codiceTributo,
    anno_riferimento: form.annoRiferimento,
    importo_a_debito_versato: form.type === 'debit' ? form.amount.toFixed(2) : null,
    importo_a_credito_compensato: form.type === 'credit' ? form.amount.toFixed(2) : null,
    rateazione_mese_rif: form.rateazioneMeseRif || null,
  },
});

const buildIMUAltriEntiPayload = (
  companyId: string,
  form: IMUAltriEntiDebitCreditFormPayload,
): IMUAltriEntiCreatePayload => ({
  ...buildBaseF24PayloadAttrs(companyId, form),
  sectionType: 'imu_altri_enti',
  tribute: form.codiceTributo,
  description: form.description || form.codiceTributo,
  details: {
    identificativo_operazione: form.identificativoOperazione || null,
    codice_ente_locale: form.codiceEnteComune || null,
    ravvedimento: form.ravv,
    immobili_variati: form.immobVariati,
    acconto: form.acc,
    saldo: form.saldo,
    numero_immobili: form.numeroImmobili || null,
    codice_tributo: form.codiceTributo,
    rateazione_mese_rif: form.rateazioneMeseRif || null,
    anno_riferimento: form.annoRiferimento || null,
    importo_a_debito_versato: form.type === 'debit' ? form.amount.toFixed(2) : null,
    importo_a_credito_compensato: form.type === 'credit' ? form.amount.toFixed(2) : null,
  },
});

const buildINAILAltriEntiPayload = (
  companyId: string,
  form: INAILAltriEntiDebitCreditFormPayload,
): INAILAltriEntiCreatePayload => ({
  ...buildBaseF24PayloadAttrs(companyId, form),
  sectionType: 'inail_altri_enti',
  tribute: form.causale,
  description: form.description || form.causale,
  details: {
    codice_sede: form.codiceSede || null,
    codice_ditta: form.codiceDitta || null,
    codice_controllo: form.codiceControllo || null,
    numero_riferimento: form.numeroRiferimento || null,
    causale: form.causale,
    importo_a_debito_versato: form.type === 'debit' ? form.amount.toFixed(2) : null,
    importo_a_credito_compensato: form.type === 'credit' ? form.amount.toFixed(2) : null,
  },
});

const buildAltriEntiPayload = (companyId: string, form: AltriEntiDebitCreditFormPayload): AltriEntiCreatePayload => ({
  ...buildBaseF24PayloadAttrs(companyId, form),
  sectionType: 'altri_enti',
  tribute: form.causaleContributo,
  description: form.description || form.causaleContributo,
  details: {
    codice_sede: form.codiceSede || null,
    codice_ente: form.codiceEnte || null,
    causale_contributo: form.causaleContributo,
    codice_posizione: form.codicePosizione,
    periodo_di_riferimento_da_mm_aaaa: form.periodoRiferimentoInizio || null,
    periodo_di_riferimento_a_mm_aaaa: form.periodoRiferimentoFine || null,
    importo_a_debito_versato: form.type === 'debit' ? form.amount.toFixed(2) : null,
    importo_a_credito_compensato: form.type === 'credit' ? form.amount.toFixed(2) : null,
  },
});

export const createTaxDebitCredit = async (
  companyId: string,
  form: CreateTaxDebitCreditFormPayload,
): Promise<TaxDebitCredit> => {
  let taxDebitCreditData: CreateTaxDebitCreditPayload;

  switch (form.sectionType) {
    case 'erario':
      taxDebitCreditData = buildErarioPayload(companyId, form);
      break;
    case 'inps':
      taxDebitCreditData = buildInpsPayload(companyId, form);
      break;
    case 'regioni':
      taxDebitCreditData = buildRegioniPayload(companyId, form);
      break;
    case 'imu_altri_enti':
      taxDebitCreditData = buildIMUAltriEntiPayload(companyId, form);
      break;
    case 'inail_altri_enti':
      taxDebitCreditData = buildINAILAltriEntiPayload(companyId, form);
      break;
    case 'altri_enti':
      taxDebitCreditData = buildAltriEntiPayload(companyId, form);
      break;
    default: {
      throw new Error(`Section type not yet implemented`);
    }
  }

  const payload = transform<CreateTaxDebitCreditPayload, {}>()
    .withInput(taxDebitCreditData)
    .withTransformer(new CreateTaxDebitCreditTransformer())
    .serialize();

  const { data } = await backendApiGateway.post<DocumentObject>('/api/v1/tax-debit-credits', payload);

  return deserialize<TaxDebitCredit>(data) as TaxDebitCredit;
};

export const getTaxDebitCreditFormValues = (
  taxDebitCredit: TaxDebitCredit | undefined,
): Partial<CreateTaxDebitCreditFormInput> | null => {
  if (!taxDebitCredit) return null;

  const isDebit = taxDebitCredit.debitAmount !== null;
  const amount = isDebit ? taxDebitCredit.debitAmount : taxDebitCredit.creditAmount;

  const base = {
    type: isDebit ? ('debit' as const) : ('credit' as const),
    description: taxDebitCredit.description ?? '',
    amount: amount?.amount,
    bookkeepingAccountId: taxDebitCredit.bookkeepingAccountId,
    scadenza: isDebit && taxDebitCredit.dueDate ? parseISO(taxDebitCredit.dueDate) : null,
    validFrom: !isDebit && taxDebitCredit.validFrom ? parseISO(taxDebitCredit.validFrom) : null,
    validUntil: !isDebit && taxDebitCredit.dueDate ? parseISO(taxDebitCredit.dueDate) : null,
  };

  switch (taxDebitCredit.sectionType) {
    case 'erario': {
      const { details } = taxDebitCredit;
      return {
        ...base,
        sectionType: 'erario',
        codiceTributo: details.codice_tributo ?? '',
        annoPeriodoRiferimento: details.anno_periodo_riferimento ?? '',
        rateazioneRegioneProvMeseRif: details.rateazione_regione_prov_mese_rif ?? '',
        codiceUfficio: details.codice_ufficio ?? '',
        codiceAtto: details.codice_atto ?? '',
      };
    }
    case 'inps': {
      const { details } = taxDebitCredit;
      return {
        ...base,
        sectionType: 'inps',
        codiceSede: details.codice_sede ?? '',
        causaleContributo: details.causale_contributo,
        matricolaCodiceFiliale: details.matricola_inps_filiale_azienda ?? '',
        periodoRiferimentoInizio: details.periodo_di_riferimento_da_mm_aaaa ?? '',
        periodoRiferimentoFine: details.periodo_di_riferimento_a_mm_aaaa ?? '',
      };
    }
    case 'regioni': {
      const { details } = taxDebitCredit;
      return {
        ...base,
        sectionType: 'regioni',
        codiceRegione: details.codice_regione ?? '',
        codiceTributo: details.codice_tributo,
        annoRiferimento: details.anno_riferimento,
        rateazioneMeseRif: details.rateazione_mese_rif ?? '',
      };
    }
    case 'imu_altri_enti': {
      const { details } = taxDebitCredit;
      return {
        ...base,
        sectionType: 'imu_altri_enti',
        identificativoOperazione: details.identificativo_operazione ?? '',
        codiceEnteComune: details.codice_ente_locale ?? '',
        ravv: details.ravvedimento,
        immobVariati: details.immobili_variati,
        acc: details.acconto,
        saldo: details.saldo,
        numeroImmobili: details.numero_immobili?.toString(),
        codiceTributo: details.codice_tributo,
        rateazioneMeseRif: details.rateazione_mese_rif ?? '',
        annoRiferimento: details.anno_riferimento ?? '',
      };
    }
    case 'inail_altri_enti': {
      const { details } = taxDebitCredit;
      return {
        ...base,
        sectionType: 'inail_altri_enti',
        codiceTributo: taxDebitCredit.tribute,
        codiceSede: details.codice_sede ?? '',
        codiceDitta: details.codice_ditta ?? '',
        codiceControllo: details.codice_controllo ?? '',
        numeroRiferimento: details.numero_riferimento ?? '',
        causale: details.causale,
      };
    }
    case 'altri_enti': {
      const { details } = taxDebitCredit;
      return {
        ...base,
        sectionType: 'altri_enti',
        codiceTributo: taxDebitCredit.tribute,
        codiceSede: details.codice_sede ?? '',
        codiceEnte: details.codice_ente ?? '',
        causaleContributo: details.causale_contributo,
        codicePosizione: details.codice_posizione,
        periodoRiferimentoInizio: details.periodo_di_riferimento_da_mm_aaaa ?? '',
        periodoRiferimentoFine: details.periodo_di_riferimento_a_mm_aaaa ?? '',
      };
    }
  }
};

export const updateTaxDebitCredit = async (
  id: string,
  companyId: string,
  form: CreateTaxDebitCreditFormPayload,
): Promise<TaxDebitCredit> => {
  let taxDebitCreditData: CreateTaxDebitCreditPayload;

  switch (form.sectionType) {
    case 'erario':
      taxDebitCreditData = buildErarioPayload(companyId, form);
      break;
    case 'inps':
      taxDebitCreditData = buildInpsPayload(companyId, form);
      break;
    case 'regioni':
      taxDebitCreditData = buildRegioniPayload(companyId, form);
      break;
    case 'imu_altri_enti':
      taxDebitCreditData = buildIMUAltriEntiPayload(companyId, form);
      break;
    case 'inail_altri_enti':
      taxDebitCreditData = buildINAILAltriEntiPayload(companyId, form);
      break;
    case 'altri_enti':
      taxDebitCreditData = buildAltriEntiPayload(companyId, form);
      break;
    default: {
      throw new Error(`Section type not yet implemented`);
    }
  }

  const payload = transform<CreateTaxDebitCreditPayload, {}>()
    .withInput(taxDebitCreditData)
    .withTransformer(new CreateTaxDebitCreditTransformer())
    .serialize();

  const { data } = await backendApiGateway.patch<DocumentObject>(`/api/v1/tax-debit-credits/${id}`, payload);

  return deserialize<TaxDebitCredit>(data) as TaxDebitCredit;
};

export const deleteTaxDebitCredit = async (id: string): Promise<void> => {
  await backendApiGateway.delete(`/api/v1/tax-debit-credits/${id}`);
};

export const fetchF24List = async (
  companyId: string,
  filters: F24QueryFilters,
  options?: { size?: number },
): Promise<F24[]> => {
  const { source } = filters;
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.eq('company.id', companyId);

  if (source) filterBuilder.in('source', source);

  const sortingBuilder = jsonAPISortBuilder([{ sort: 'createdAt', direction: 'desc' }]);
  const includeBuilder = jsonAPIIncludeBuilder(['sourceUpload.pages']);
  const params = (() => {
    if (!options?.size) return buildJsonAPIQueryParams(filterBuilder, sortingBuilder, includeBuilder);

    const paginatorBuilder = jsonAPICursorPaginatorBuilder();
    paginatorBuilder.size(options.size);

    return buildJsonAPIQueryParams(filterBuilder, sortingBuilder, paginatorBuilder, includeBuilder);
  })();

  const { data } = await backendApiGateway.get<DocumentObject>('/api/v1/f24', { params });

  const deserialized = deserialize<F24>(data) as F24[];

  return deserialized;
};

export const fetchF24 = async (id: string): Promise<F24> => {
  const { data } = await backendApiGateway.get<DocumentObject>(`/api/v1/f24/${id}`);
  return deserialize<F24>(data) as F24;
};

export const fetchF24WithPages = async (id: string): Promise<F24> => {
  const includeBuilder = jsonAPIIncludeBuilder(['sourceUpload.pages.receipts', 'account', 'directDebitCollection']);
  const params = buildJsonAPIQueryParams(includeBuilder);
  const { data } = await backendApiGateway.get<DocumentObject>(`/api/v1/f24/${id}`, { params });
  return deserialize<F24>(data) as F24;
};

export const deleteF24 = async (id: string): Promise<void> => {
  await backendApiGateway.delete(`/api/v1/f24/${id}`);
};

export const fetchF24Details = async (id: string): Promise<F24Details> => {
  const { data } = await backendApiGateway.get(`/api/v1/f24/${id}/details`);
  return normalizeF24Details(data);
};

const getTaxDebitCreditIds = async (companyId: string, payload: F24CreatePayload) => {
  // create repentance debts
  const repentanceDebts = await Promise.all(
    [...payload.sanctions, ...payload.interests].map((tax) => createTaxDebitCredit(companyId, tax)),
  );

  return [
    ...repentanceDebts.map((debit) => ({ id: debit.id, type: TaxDebitCreditResourceType })),
    ...payload.debits.map((debit) => ({ id: debit.id, type: TaxDebitCreditResourceType })),
    ...payload.credits.map((credit) => ({ id: credit.id, type: TaxDebitCreditResourceType })),
  ];
};

export const createF24 = async (
  companyId: string,
  payload: F24CreatePayload | F24UploadCreatePayload,
): Promise<F24> => {
  const f24Data = {
    source: payload.source,
    paymentDate: formatDateForAPI(payload.paymentDate),
    company: { id: companyId },
    ...(payload.source === F24Source.BOOKKEEPING && {
      taxDebitCredits: await getTaxDebitCreditIds(companyId, payload),
      account: { id: payload.accountId },
      iban: payload.accountIban,
      totalDebits: payload.totalDebits,
      totalCredits: payload.totalCredits,
    }),
    ...(payload.source === F24Source.UPLOAD && {
      taxDebitCredits: [],
      pdfFilename: payload.pdfFilename ?? null,
      f24UploadId: payload.f24UploadId,
      name: payload.name,
      ...(payload.account ? { account: payload.account } : {}),
      conditionsAccepted: payload.conditionsAccepted,
      specificClausesAccepted: payload.specificClausesAccepted,
    }),
  };

  const serializedPayload = serialize(f24Data, F24ResourceType, {
    relationships: ['company', 'taxDebitCredits', ...('account' in f24Data ? ['account'] : [])],
  });

  const { data } = await backendApiGateway.post<DocumentObject>('/api/v1/f24', serializedPayload);

  return deserialize<F24>(data) as F24;
};

interface FetchCodiciTributoParams {
  search?: string;
  section?: CodiceTributoSection;
  modality?: CodiceTributoModality;
  codes?: string[];
}

interface FetchCodiciTributoResponse {
  data: CodiceTributo[];
}

export const fetchCodiciTributo = async (params: FetchCodiciTributoParams): Promise<FetchCodiciTributoResponse> => {
  const filterBuilder = jsonAPIFilterBuilder();
  const extraQueryBuilder = jsonAPIExtraQueryBuilder();

  if (params.search) {
    extraQueryBuilder.add('search', params.search);
  }

  if (params.section) {
    extraQueryBuilder.add('section', params.section);
  }

  if (params.modality) {
    extraQueryBuilder.add('modality', params.modality);
  }

  if (params.codes && isNotEmpty(params.codes)) {
    extraQueryBuilder.add('codes', params.codes.join(','));
  }

  const queryParams = buildJsonAPIQueryParams(filterBuilder, extraQueryBuilder);

  const { data } = await backendApiGateway.get<FetchCodiciTributoResponse>('/api/v1/f24/codici-tributo', {
    params: queryParams,
  });

  return data;
};

interface FetchCodiciRegioneParams {
  search?: string;
  modality?: string;
}

interface FetchCodiciRegioneResponse {
  data: CodiceRegione[];
}

export const fetchCodiciRegione = async (params: FetchCodiciRegioneParams): Promise<FetchCodiciRegioneResponse> => {
  const filterBuilder = jsonAPIFilterBuilder();
  const extraQueryBuilder = jsonAPIExtraQueryBuilder();

  if (params.search) {
    extraQueryBuilder.add('search', params.search);
  }

  const queryParams = buildJsonAPIQueryParams(filterBuilder, extraQueryBuilder);

  const { data } = await backendApiGateway.get<FetchCodiciRegioneResponse>('/api/v1/f24/codici-regioni', {
    params: queryParams,
  });

  return data;
};

interface FetchCodiciSedeINPSParams {
  search?: string;
}

interface FetchCodiciSedeINPSResponse {
  data: CodiceSedeINPS[];
}

export const fetchCodiciSedeINPS = async (params: FetchCodiciSedeINPSParams): Promise<FetchCodiciSedeINPSResponse> => {
  const filterBuilder = jsonAPIFilterBuilder();
  const extraQueryBuilder = jsonAPIExtraQueryBuilder();

  if (params.search) {
    extraQueryBuilder.add('search', params.search);
  }

  const queryParams = buildJsonAPIQueryParams(filterBuilder, extraQueryBuilder);

  const { data } = await backendApiGateway.get<FetchCodiciSedeINPSResponse>('/api/v1/f24/codici-sede-inps', {
    params: queryParams,
  });

  return data;
};

interface FetchCausaliContributoINPSParams {
  search?: string;
  modality?: string;
}

interface FetchCausaliContributoINPSResponse {
  data: CausaleContributoINPS[];
}

export const fetchCausaliContributoINPS = async (
  params: FetchCausaliContributoINPSParams,
): Promise<FetchCausaliContributoINPSResponse> => {
  const filterBuilder = jsonAPIFilterBuilder();
  const extraQueryBuilder = jsonAPIExtraQueryBuilder();

  if (params.search) {
    extraQueryBuilder.add('search', params.search);
  }

  if (params.modality) {
    extraQueryBuilder.add('modality', params.modality);
  }

  const queryParams = buildJsonAPIQueryParams(filterBuilder, extraQueryBuilder);

  const { data } = await backendApiGateway.get<FetchCausaliContributoINPSResponse>('/api/v1/f24/codici-causali-inps', {
    params: queryParams,
  });

  return data;
};
